import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { ScoreCard, ClubCard, CtaCard } from '../../cards';
import AdWrapper from '../../Ads/AdWrapper';
import Events from '../EventsTabContent/Events';
import LoadingText from '../../text/LoadingText';
import PerformanceStatsResults from './PerformanceStatsResults';

import useIsMobile from '../../hooks/useIsMobile';
import { AdTypes } from '../../../utils/types';
import { EventScheduleTypes } from '../../../utils/eventConstants';
import { EventTypes } from '../../../utils/clubs';
import { getFindEventsSearchUrl, filterRenderableProfileEvents } from '../../../utils/events';
import { getOverviewData, getClubsData } from '../../../actions/profile-tab-actions';
import { screens } from '../../PopUps/LoginPopUpWrapper/LoginPopUp/utils';
import { useAdDynamicConfig } from '../../hooks/useAdDynamicConfig';
import { useStatsig } from '../../hooks/useStatsig';
import { ImportedEventEntryPoints } from '../../Analytics/constants';

import styles from '../../../pages/ProfilePage/profilePage.scss';

function OverviewTabContent({
    profile,
    auth,
    isProfileIdUpdated,
    isCurrentUserProfile,
    isProProfile,
    isUserLoggedIn,
    ctaData,
    search,
    onSeeEventsClick,
    onShowLoginPopUp,
    isPerformanceStatsEnabled,
    isStatsTabVisible,
}) {
    const history = useHistory();
    const dispatch = useDispatch();
    const [hasMountedWithoutUser, setHasMountedWithoutUser] = useState(false);
    const { data, isLoading } = useSelector(({ profileTabData }) => profileTabData?.overview);
    const { data: clubData } = useSelector(({ profileTabData }) => profileTabData?.clubs);
    const performanceStatsOverview = useSelector(({ profile }) => profile?.performanceStatsOverview);
    const { RECENT, UPCOMING } = EventScheduleTypes;
    const { results, recentEvents, upcomingEvents } = data || {};
    const { recentClubs } = clubData || {};

    const { checkGate, featureGates } = useStatsig();
    const isImportedEventPagesEnabled = checkGate(featureGates.IMPORTED_EVENT_PAGES);
    const visibleUpcomingEvents = filterRenderableProfileEvents(upcomingEvents, isImportedEventPagesEnabled);
    const visibleRecentEvents = filterRenderableProfileEvents(recentEvents, isImportedEventPagesEnabled);

    const { isMobileWidth } = useIsMobile();
    const { showAds, playerProfileConfig } = useAdDynamicConfig();
    const showRectangleAds = showAds && isMobileWidth && playerProfileConfig?.showRectangle;

    useEffect(() => {
        if (!isUserLoggedIn) {
            setHasMountedWithoutUser(true);
        }
    }, []);

    useEffect(() => {
        if (isUserLoggedIn && !data && isProfileIdUpdated && !hasMountedWithoutUser) {
            dispatch(getOverviewData());
        }
        if (isUserLoggedIn && !recentClubs && isProfileIdUpdated && !hasMountedWithoutUser) {
            dispatch(getClubsData());
        }
    }, [isProfileIdUpdated, isUserLoggedIn, hasMountedWithoutUser]);

    let eligibleResults;

    if (profile?.data?.extendedRatingProfile?.eligibleResults) {
        eligibleResults = [...profile.data.extendedRatingProfile.eligibleResults.singles, ...profile.data.extendedRatingProfile.eligibleResults.doubles];
    }

    const handleOnFindEventsClick = () => {
        const url = getFindEventsSearchUrl(search);
        history.push(url);
    };
    const firstName = profile?.data?.firstName;
    const lastCharacter = firstName.charAt(firstName.length - 1);
    const isNameEndingInS = lastCharacter.toLowerCase() === 's';
    const name = isNameEndingInS ? `${firstName}'` : `${firstName}'s`;

    const showPerformanceDesign = isPerformanceStatsEnabled && isUserLoggedIn && isStatsTabVisible;
    const isPerformanceResolved = !!performanceStatsOverview?.hasLoaded;
    const hasPerformanceResults = performanceStatsOverview?.data?.results?.length > 0;

    const postScoreCtaNode = (isCurrentUserProfile && visibleRecentEvents?.length < 2) ? (
        <div className={`col-12 col-md-6`}>
            <CtaCard
                data={ctaData?.POST_SCORE}
                onCtaClick={() => {
                    history.push('/post-score-select');
                }}
                className="score-card"
                clickContext="Results tab - Post score button"
                clickLocation="Player Profile CTA"
            />
        </div>
    ) : null;

    const renderLegacyRecentResults = () => (
        <>
            <h6 className={`title`} data-testid="profile.overview.recent-results-title">Recent Results</h6>
            <div className="row">
                {(results?.length ? results.map((result) => {
                    const isSingles = !result?.players?.winner2 && !result?.players?.loser2;
                    return (
                        <div
                            key={`score-${result.id}`}
                            className="col-12 col-md-6"
                        >
                            <ScoreCard
                                {...result}
                                playerId={profile.playerId}
                                showDecimalForOwn={true}
                                gameType={isSingles ? 'singles' : 'doubles'}
                                eligibleResults={eligibleResults}
                                forceCardView={true}
                                timeZoneValue={'UTC'}
                                showVerified={false}
                                isProProfile={isProProfile}
                                isLeaguesMatch={result?.type === EventTypes.LEAGUES}
                                isProfileOverviewResult={true}
                                data-testid={`profile.overview.score-${result.id}`}
                            />
                        </div>
                    );
                }) : (
                    <div className={`col-12`}>
                        <p className={`text-center p48`} data-testid="profile.overview.no-results-text">No Recent Results Available</p>
                    </div>
                )
                )}
                {postScoreCtaNode}
            </div>
        </>
    );

    const renderGatedLegacyRecentResults = () => {
        if (!(isCurrentUserProfile || results?.length > 0 || !auth.user)) { return null; }
        return renderLegacyRecentResults();
    };

    const renderRecentResults = () => {
        if (!showPerformanceDesign) { return renderGatedLegacyRecentResults(); }
        if (!isPerformanceResolved) {
            return (
                <>
                    <h6 className={`title`} data-testid="profile.overview.performance-results-loading-title">Recent Results</h6>
                    <div className="pt40" data-testid="profile.overview.performance-results-loading-wrapper">
                        <LoadingText text={'Loading Results'} />
                    </div>
                </>
            );
        }
        if (!hasPerformanceResults) { return renderGatedLegacyRecentResults(); }
        return (
            <PerformanceStatsResults
                playerId={profile.playerId}
                data={performanceStatsOverview.data}
                auth={auth}
                isOwnProfile={isCurrentUserProfile}
                ctaCard={postScoreCtaNode}
                data-testid="profile.overview.performance-results"
            />
        );
    };

    if (!isUserLoggedIn) {
        return (
            <div className={`${styles.overviewSignInContainer}`} data-testid="profile.overview.sign-in-container">
                <p className={`${styles.overviewSignInTitle}`} data-testid="profile.overview.sign-in-title">{`Sign in or sign up to view ${name} profile`}</p>
                <p className={`${styles.overviewSignInSubtitle}`} data-testid="profile.overview.sign-in-subtitle">{`View ${name} match results, upcoming and previous events, player stats, and other information.`}</p>
                <button
                    className={`btn btn-primary btn-block btn-xl text-uppercase mt16`}
                    onClick={() => onShowLoginPopUp('signin', screens.LOGIN)}
                    type="button"
                    data-testid="profile.overview.sign-in-btn"
                >
                    Sign In
                </button>
                <button
                    className={`btn btn-primary btn-block btn-xl text-uppercase mt16`}
                    onClick={() => onShowLoginPopUp('signin', screens.SIGN_UP)}
                    type="button"
                    data-testid="profile.overview.sign-up-btn"
                >
                    Sign Up
                </button>
            </div>
        );
    }

    return (
        <div>
            {isLoading ? (
                <div className="pt40" data-testid="profile.overview.loading-wrapper">
                    <LoadingText text={'Loading Overview'} />
                </div>
            ) : (
                <div className={`${styles.overview}`} data-testid="profile.overview.content">
                    {showRectangleAds && (
                        <div className="d-flex jcc mt24 mb24">
                            <AdWrapper type={AdTypes.RECTANGLE_ONE} data-testid="profile.overview.ad.rectangle-1" />
                        </div>
                    )}
                    {renderRecentResults()}
                    {isUserLoggedIn && visibleUpcomingEvents.length > 0 && (
                        <>
                            <h6 className={`title`} data-testid="profile.overview.upcoming-events-title">Upcoming Events</h6>
                            <Events
                                auth={auth}
                                isCurrentUserProfile={isCurrentUserProfile}
                                showAll={false}
                                type={UPCOMING}
                                events={visibleUpcomingEvents}
                                force={{ cardView: true }}
                                search={search}
                                onHandleTabClick={onSeeEventsClick}
                                ctaData={ctaData}
                                isProfilePage={true}
                                entryPoint={ImportedEventEntryPoints.OVERVIEW_TAB_CARD}
                            />
                        </>
                    )}
                    {isUserLoggedIn && visibleRecentEvents.length > 0 && (
                        <>
                            <h6 className={`title`} data-testid="profile.overview.recent-events-title">Recent Events</h6>
                            <Events
                                auth={auth}
                                isCurrentUserProfile={isCurrentUserProfile}
                                showAll={false}
                                type={RECENT}
                                events={visibleRecentEvents}
                                force={{ cardView: true }}
                                search={search}
                                onHandleTabClick={onSeeEventsClick}
                                ctaData={ctaData}
                                entryPoint={ImportedEventEntryPoints.OVERVIEW_TAB_CARD}
                            />
                        </>
                    )}
                    {showRectangleAds && (
                        <div className="d-flex jcc mt24 mb24">
                            <AdWrapper type={AdTypes.RECTANGLE_TWO} data-testid="profile.overview.ad.rectangle-2" />
                        </div>
                    )}
                    {isCurrentUserProfile && !(visibleRecentEvents.length || visibleUpcomingEvents.length > 0) && (
                        <>
                            <h6 className={`title`} data-testid="profile.overview.upcoming-events-title">Upcoming Events</h6>
                            <div className="row">
                                <div className={`col-12 col-md-6`}>
                                    <CtaCard
                                        data={ctaData.FIND_EVENTS}
                                        onCtaClick={handleOnFindEventsClick}
                                        className="event-card"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    {(isUserLoggedIn && recentClubs !== null) && (
                        <>
                            {(isCurrentUserProfile || (recentClubs?.length > 0)) && (
                                <>
                                    <h6 className={`title`} data-testid="profile.overview.clubs-title">Clubs</h6>
                                    <div className="row">
                                        {recentClubs.map((club) => (
                                            <div
                                                className={`col-12 col-lg-6`}
                                                key={`club-${club.id}`}
                                            >
                                                <ClubCard
                                                    club={club}
                                                    force={{ cardView: true }}
                                                    data-testid={`profile.overview.club-${club.id}`}
                                                />
                                            </div>
                                        ))}
                                        {(recentClubs?.length === 0 && auth.user.isPlayer) && (
                                            <div className={`col-12 col-lg-6`}>
                                                <CtaCard
                                                    data={ctaData.CREATE_PERSONAL_CLUB}
                                                    onCtaClick={() => {
                                                        history.push('/create-players-group');
                                                    }}
                                                    className="club-card"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

OverviewTabContent.propTypes = {
    auth: PropTypes.shape({
        user: PropTypes.shape({
            id: PropTypes.number,
            isPlayer: PropTypes.bool,
        }),
    }).isRequired,
    isPerformanceStatsEnabled: PropTypes.bool,
    isStatsTabVisible: PropTypes.bool,
};

OverviewTabContent.defaultProps = {
    isPerformanceStatsEnabled: false,
    isStatsTabVisible: true,
};

export default OverviewTabContent;
